import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Megaphone, Users, RefreshCw, Send, Bell, Loader2, Search, Download,
  AlertCircle, CheckCircle2, XCircle, TrendingUp, Clock, UserCheck,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEGMENT_CARDS = [
  {
    id: 'abandoned_searchers',
    label: 'Abandoned Searchers',
    desc: 'Searched in last 7 days but no booking — ripe for re-engagement',
    color: 'from-orange-500 to-red-500',
    icon: Clock,
  },
  {
    id: 'high_value',
    label: 'High Value',
    desc: '3+ bookings OR ₹50k+ lifetime spend',
    color: 'from-emerald-500 to-teal-500',
    icon: TrendingUp,
  },
  {
    id: 'inactive_30d',
    label: 'Inactive 30+ Days',
    desc: 'Registered users who haven\'t returned in a month',
    color: 'from-slate-500 to-slate-700',
    icon: UserCheck,
  },
  {
    id: 'no_bookings',
    label: 'No Bookings',
    desc: 'Browsed but never converted',
    color: 'from-indigo-500 to-purple-500',
    icon: Users,
  },
];

const MarketingDashboard = () => {
  const [tab, setTab] = useState('segments');
  const [summary, setSummary] = useState({});
  const [profiles, setProfiles] = useState([]);
  const [segment, setSegment] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [history, setHistory] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [subsTotal, setSubsTotal] = useState(0);

  // Push composer state
  const [pushTargetSegment, setPushTargetSegment] = useState('abandoned_searchers');
  const [pushTitle, setPushTitle] = useState('');
  const [pushBody, setPushBody] = useState('');
  const [pushUrl, setPushUrl] = useState('/');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const loadSummary = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/marketing/profiles/segments/summary`, { withCredentials: true });
      setSummary(r.data.segments || {});
    } catch (e) { /* ignore */ }
  }, []);

  const loadProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/marketing/profiles`, {
        withCredentials: true,
        params: { page, limit: 25, segment, search },
      });
      setProfiles(r.data.profiles || []);
      setTotal(r.data.total || 0);
    } catch (e) { setProfiles([]); }
    setLoading(false);
  }, [page, segment, search]);

  const loadHistory = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/push/history`, { withCredentials: true, params: { limit: 20 } });
      setHistory(r.data.broadcasts || []);
    } catch (e) { /* ignore */ }
  }, []);

  const loadSubscriptions = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/push/subscriptions`, { withCredentials: true, params: { page: 1, limit: 50 } });
      setSubscriptions(r.data.subscriptions || []);
      setSubsTotal(r.data.total || 0);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { loadSummary(); loadHistory(); loadSubscriptions(); }, [loadSummary, loadHistory, loadSubscriptions]);
  useEffect(() => { if (tab === 'profiles') loadProfiles(); }, [tab, loadProfiles]);

  const refreshProfiles = async () => {
    setRefreshing(true);
    try {
      const r = await axios.post(`${API}/admin/marketing/profiles/refresh`, {}, { withCredentials: true });
      alert(`Refreshed: scanned ${r.data.stats.scanned_users} users, upserted ${r.data.stats.upserted}`);
      await loadSummary();
      if (tab === 'profiles') await loadProfiles();
    } catch (e) {
      alert('Refresh failed: ' + (e.response?.data?.detail || e.message));
    }
    setRefreshing(false);
  };

  const sendPush = async () => {
    if (!pushTitle.trim() || !pushBody.trim()) {
      alert('Title and body are required');
      return;
    }
    if (!window.confirm(`Send push notification to "${pushTargetSegment}" segment (${summary[pushTargetSegment] || 0} users)?`)) return;
    setSending(true);
    setSendResult(null);
    try {
      const r = await axios.post(
        `${API}/admin/push/send-segment`,
        { segment: pushTargetSegment, title: pushTitle, body: pushBody, url: pushUrl },
        { withCredentials: true },
      );
      setSendResult({ ok: true, ...r.data.broadcast });
      setPushTitle('');
      setPushBody('');
      setPushUrl('/');
      await loadHistory();
    } catch (e) {
      setSendResult({ ok: false, error: e.response?.data?.detail || e.message });
    }
    setSending(false);
  };

  const exportCsv = (seg) => {
    window.open(`${API}/admin/marketing/profiles/export/csv?segment=${seg}`, '_blank');
  };

  const totalPages = Math.max(1, Math.ceil(total / 25));

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex items-start md:items-center justify-between mb-6 flex-col md:flex-row gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Megaphone className="w-7 h-7 text-orange-500" /> Marketing
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            User segments, push notifications & re-engagement campaigns
          </p>
        </div>
        <button
          data-testid="refresh-profiles-btn"
          onClick={refreshProfiles}
          disabled={refreshing}
          className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium disabled:opacity-50"
        >
          {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Refresh Profiles
        </button>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 mb-6 bg-white border border-gray-200 rounded-xl p-1 w-fit">
        {[
          { id: 'segments', label: 'Segments', icon: Users },
          { id: 'profiles', label: 'Profiles', icon: Search },
          { id: 'push', label: 'Push Notification', icon: Bell },
          { id: 'history', label: 'History', icon: Clock },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`marketing-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
              tab === t.id
                ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-sm'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Segments Tab ─────────────────────────────────────────────── */}
      {tab === 'segments' && (
        <div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {SEGMENT_CARDS.map((s) => (
              <div
                key={s.id}
                data-testid={`segment-card-${s.id}`}
                className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition"
              >
                <div className={`w-11 h-11 rounded-lg bg-gradient-to-br ${s.color} flex items-center justify-center text-white mb-3`}>
                  <s.icon className="w-5 h-5" />
                </div>
                <h3 className="font-semibold text-gray-900">{s.label}</h3>
                <p className="text-3xl font-bold text-gray-900 mt-2">{summary[s.id] ?? '—'}</p>
                <p className="text-xs text-gray-500 mt-1 min-h-[32px]">{s.desc}</p>
                <div className="flex gap-2 mt-3">
                  <button
                    data-testid={`view-profiles-${s.id}`}
                    onClick={() => { setSegment(s.id); setPage(1); setTab('profiles'); }}
                    className="flex-1 text-xs bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-md text-gray-700"
                  >
                    View
                  </button>
                  <button
                    data-testid={`push-to-${s.id}`}
                    onClick={() => { setPushTargetSegment(s.id); setTab('push'); }}
                    className="flex-1 text-xs bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 rounded-md"
                  >
                    Send Push
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <SummaryPill label="Total Profiles" value={summary.total || 0} />
            <SummaryPill label="Subscribed (consent)" value={summary.subscribed || 0} />
            <SummaryPill label="Unsubscribed" value={summary.unsubscribed || 0} />
            <SummaryPill label="Push Subscribers" value={subsTotal || 0} />
          </div>
        </div>
      )}

      {/* ── Profiles Tab ─────────────────────────────────────────────── */}
      {tab === 'profiles' && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="p-4 flex flex-col md:flex-row gap-3 border-b border-gray-200">
            <select
              data-testid="profile-segment-filter"
              value={segment}
              onChange={(e) => { setSegment(e.target.value); setPage(1); }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="all">All Profiles</option>
              <option value="abandoned_searchers">Abandoned Searchers</option>
              <option value="high_value">High Value</option>
              <option value="inactive_30d">Inactive 30+ Days</option>
              <option value="no_bookings">No Bookings</option>
              <option value="subscribed">Subscribed</option>
              <option value="unsubscribed">Unsubscribed</option>
            </select>
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                data-testid="profile-search-input"
                type="text"
                placeholder="Search by name, email, phone"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm w-full"
              />
            </div>
            <button
              data-testid="export-csv-btn"
              onClick={() => exportCsv(segment)}
              className="ml-auto bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 text-gray-700"
            >
              <Download className="w-4 h-4" /> CSV
            </button>
          </div>
          {loading ? (
            <div className="p-12 text-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : profiles.length === 0 ? (
            <div className="p-12 text-center text-gray-500 text-sm">No profiles in this segment yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="px-4 py-2 font-medium text-gray-600">User</th>
                    <th className="px-4 py-2 font-medium text-gray-600">Type</th>
                    <th className="px-4 py-2 font-medium text-gray-600">Searches</th>
                    <th className="px-4 py-2 font-medium text-gray-600">Bookings</th>
                    <th className="px-4 py-2 font-medium text-gray-600">Revenue</th>
                    <th className="px-4 py-2 font-medium text-gray-600">Last Sector</th>
                    <th className="px-4 py-2 font-medium text-gray-600">Last Active</th>
                    <th className="px-4 py-2 font-medium text-gray-600">Flags</th>
                  </tr>
                </thead>
                <tbody>
                  {profiles.map((p) => (
                    <tr key={p.user_id} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-2">
                        <div className="font-medium text-gray-900">{p.name || '—'}</div>
                        <div className="text-xs text-gray-500">{p.email || p.phone || p.user_id.slice(0, 8)}</div>
                      </td>
                      <td className="px-4 py-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${p.user_type === 'agent' ? 'bg-indigo-100 text-indigo-700' : 'bg-emerald-100 text-emerald-700'}`}>
                          {p.user_type}
                        </span>
                      </td>
                      <td className="px-4 py-2">{p.search_count_total}</td>
                      <td className="px-4 py-2">{p.booking_count}</td>
                      <td className="px-4 py-2">₹{(p.booking_revenue || 0).toLocaleString()}</td>
                      <td className="px-4 py-2 text-xs">{p.last_search_sector || '—'}</td>
                      <td className="px-4 py-2 text-xs">
                        {p.days_inactive != null ? `${p.days_inactive}d ago` : '—'}
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex gap-1 flex-wrap">
                          {p.is_abandoned_searcher && <FlagBadge color="orange" label="Abandoned" />}
                          {p.is_high_value && <FlagBadge color="emerald" label="High Value" />}
                          {p.is_inactive_30d && <FlagBadge color="slate" label="Inactive" />}
                          {!p.marketing_consent && <FlagBadge color="red" label="Unsub" />}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {totalPages > 1 && (
            <div className="p-3 border-t border-gray-200 flex items-center justify-between text-sm">
              <span className="text-gray-500">Page {page} / {totalPages} · {total} profiles</span>
              <div className="flex gap-2">
                <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="px-3 py-1 border rounded disabled:opacity-50">Prev</button>
                <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages} className="px-3 py-1 border rounded disabled:opacity-50">Next</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Push Notification Tab ────────────────────────────────────── */}
      {tab === 'push' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Composer */}
          <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl p-5">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Bell className="w-5 h-5 text-orange-500" /> Compose Push Notification
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Target Segment</label>
                <select
                  data-testid="push-segment-select"
                  value={pushTargetSegment}
                  onChange={(e) => setPushTargetSegment(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="abandoned_searchers">Abandoned Searchers ({summary.abandoned_searchers || 0})</option>
                  <option value="high_value">High Value ({summary.high_value || 0})</option>
                  <option value="inactive_30d">Inactive 30+ Days ({summary.inactive_30d || 0})</option>
                  <option value="no_bookings">No Bookings ({summary.no_bookings || 0})</option>
                  <option value="subscribed">All Subscribed ({summary.subscribed || 0})</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Title ({pushTitle.length}/120)</label>
                <input
                  data-testid="push-title-input"
                  type="text"
                  maxLength={120}
                  value={pushTitle}
                  onChange={(e) => setPushTitle(e.target.value)}
                  placeholder="Your flight BOM → DEL is at lowest price!"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Body ({pushBody.length}/400)</label>
                <textarea
                  data-testid="push-body-input"
                  maxLength={400}
                  value={pushBody}
                  onChange={(e) => setPushBody(e.target.value)}
                  placeholder="We saved your search. Tap to book now — 3 seats left."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Click URL</label>
                <input
                  data-testid="push-url-input"
                  type="text"
                  value={pushUrl}
                  onChange={(e) => setPushUrl(e.target.value)}
                  placeholder="/"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <button
                data-testid="send-push-btn"
                onClick={sendPush}
                disabled={sending || !pushTitle.trim() || !pushBody.trim()}
                className="w-full md:w-auto bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white px-6 py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Send to {summary[pushTargetSegment] || 0} users
              </button>
              {sendResult && (
                <div className={`p-3 rounded-lg text-sm ${sendResult.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'}`}>
                  {sendResult.ok ? (
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <div>
                        <strong>Broadcast sent</strong> — targeted {sendResult.stats?.subscriptions_targeted || 0} devices, delivered {sendResult.stats?.sent || 0}, failed {sendResult.stats?.failed || 0}, removed dead {sendResult.stats?.removed || 0}.
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2">
                      <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <div><strong>Error:</strong> {sendResult.error}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Preview */}
          <div className="bg-gray-900 rounded-xl p-5 text-white">
            <h4 className="font-semibold mb-3 text-sm text-gray-300">Preview</h4>
            <div className="bg-gray-800 rounded-lg p-4 shadow-lg">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded bg-orange-500 flex items-center justify-center flex-shrink-0">
                  <Bell className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{pushTitle || 'Your notification title'}</div>
                  <div className="text-xs text-gray-300 mt-1 line-clamp-3">{pushBody || 'Your notification message body will appear here.'}</div>
                  <div className="text-[10px] text-gray-500 mt-2">acftourism.com · now</div>
                </div>
              </div>
            </div>
            <div className="mt-4 text-xs text-gray-400 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              Only users who granted notification permission will receive this.
              Currently {subsTotal} active subscriptions.
            </div>
          </div>
        </div>
      )}

      {/* ── History Tab ─────────────────────────────────────────────── */}
      {tab === 'history' && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="px-4 py-2 font-medium text-gray-600">Sent</th>
                  <th className="px-4 py-2 font-medium text-gray-600">Segment</th>
                  <th className="px-4 py-2 font-medium text-gray-600">Title</th>
                  <th className="px-4 py-2 font-medium text-gray-600">Devices</th>
                  <th className="px-4 py-2 font-medium text-gray-600">Sent</th>
                  <th className="px-4 py-2 font-medium text-gray-600">Failed</th>
                  <th className="px-4 py-2 font-medium text-gray-600">Removed</th>
                </tr>
              </thead>
              <tbody>
                {history.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No broadcasts yet.</td></tr>
                ) : history.map((b) => (
                  <tr key={b.id} className="border-t border-gray-100">
                    <td className="px-4 py-2 text-xs">{new Date(b.sent_at).toLocaleString()}</td>
                    <td className="px-4 py-2 text-xs"><span className="bg-gray-100 px-2 py-0.5 rounded">{b.segment}</span></td>
                    <td className="px-4 py-2 font-medium">{b.title}</td>
                    <td className="px-4 py-2">{b.stats?.subscriptions_targeted || 0}</td>
                    <td className="px-4 py-2 text-emerald-600">{b.stats?.sent || 0}</td>
                    <td className="px-4 py-2 text-red-600">{b.stats?.failed || 0}</td>
                    <td className="px-4 py-2 text-gray-500">{b.stats?.removed || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const SummaryPill = ({ label, value }) => (
  <div className="bg-white border border-gray-200 rounded-lg px-3 py-2">
    <div className="text-xs text-gray-500">{label}</div>
    <div className="text-xl font-bold text-gray-900">{value}</div>
  </div>
);

const FlagBadge = ({ color, label }) => {
  const colors = {
    orange: 'bg-orange-100 text-orange-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    slate: 'bg-slate-100 text-slate-700',
    red: 'bg-red-100 text-red-700',
  };
  return <span className={`text-[10px] px-1.5 py-0.5 rounded ${colors[color]}`}>{label}</span>;
};

export default MarketingDashboard;
